/**
 * Step5TargetInsurers — création optionnelle de QR draft (9.T.5.v3.6, QA §6).
 *
 * QA §6 v3.6 :
 *   - target_insurers uniques (unicité enforced Zod côté shared v3.5)
 *   - Toggle OFF par défaut (aucune QR créée si off)
 *   - Aucun envoi email dans ce flow (invariant §6.5 backend)
 *   - Résumé Step 6 précise "draft seulement"
 */

import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { InsurerCatalogBoundary } from "../../../../components/InsurerCatalogBoundary";
import { useInsurerCatalog } from "../../../../lib/queries/insurers";

import type { WizardData } from "./MaterializeWizard";

type Props = {
  data: WizardData;
  onChange: (patch: Partial<WizardData>) => void;
};

export function Step5TargetInsurers({ data, onChange }: Props) {
  const { t } = useTranslation();
  const insurerCatalog = useInsurerCatalog();
  const enabled = data.createQuoteRequestEnabled ?? false;
  const targets = data.createQuoteRequest?.target_insurers ?? [];

  // 9.T.5.v3.6 QA post-fix #7 — propagation `applyProjectData.insurer_codes`
  // (canoniques Q7.a hydratés Step 4) vers `createQuoteRequest.target_insurers`
  // au premier montage Step 5 si aucun target_insurers déjà défini.
  // Idempotent : ne re-écrase pas les choix manuels du broker post-toggle.
  // Ne touche pas si le broker a explicitement vidé la liste (undefined vs []).
  useEffect(() => {
    const canonicalInsurers = data.applyProjectData?.insurer_codes ?? [];
    if (canonicalInsurers.length === 0) return;
    if (data.createQuoteRequest?.target_insurers !== undefined) return; // broker a déjà touché
    onChange({
      createQuoteRequest: {
        ...(data.createQuoteRequest ?? {}),
        target_insurers: [...canonicalInsurers],
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.applyProjectData?.insurer_codes]);

  const projectResolved =
    data.strategy === "attach_existing_project"
      ? !!data.existingProjectId
      : !!data.newProject?.title;

  const toggleInsurer = (code: string) => {
    const next = targets.includes(code) ? targets.filter((c) => c !== code) : [...targets, code];
    onChange({
      createQuoteRequest: {
        ...(data.createQuoteRequest ?? {}),
        target_insurers: next,
      },
    });
  };

  return (
    <section>
      <h3 className="mb-1 text-lg font-medium text-slate-900">
        {t("intake_events.detail.materialize.wizard.step_5_target_insurers.title")}
      </h3>
      <p className="mb-4 text-sm text-slate-600">
        {t("intake_events.detail.materialize.wizard.step_5_target_insurers.subtitle")}
      </p>

      {!projectResolved && (
        <p
          className="mb-3 rounded-md border border-amber-200 bg-amber-50 p-2 text-sm text-amber-900"
          role="alert"
        >
          {t("intake_events.detail.materialize.wizard.step_5_target_insurers.requires_project")}
        </p>
      )}

      <label className="focus-within:ring-brand-500 mb-4 flex items-start gap-3 rounded-md border border-slate-200 p-3 focus-within:ring-2 hover:bg-slate-50">
        <input
          type="checkbox"
          checked={enabled}
          disabled={!projectResolved}
          onChange={(e) => onChange({ createQuoteRequestEnabled: e.target.checked })}
          className="focus-visible:ring-brand-500 mt-1 focus-visible:ring-2"
        />
        <span className="text-sm">
          <span className="block font-medium text-slate-800">
            {t("intake_events.detail.materialize.wizard.step_5_target_insurers.toggle_label")}
          </span>
          <span className="block text-xs text-slate-500">
            {t("intake_events.detail.materialize.wizard.step_5_target_insurers.toggle_hint")}
          </span>
        </span>
      </label>

      {enabled && projectResolved && (
        <div className="space-y-3">
          <fieldset>
            <legend className="mb-1 text-xs font-medium text-slate-600">
              {t(
                "intake_events.detail.materialize.wizard.step_5_target_insurers.target_insurers_label",
              )}
            </legend>
            <p className="mb-2 text-xs text-slate-500">
              {t(
                "intake_events.detail.materialize.wizard.step_5_target_insurers.target_insurers_hint",
              )}
            </p>
            <InsurerCatalogBoundary catalog={insurerCatalog} renderChildrenWhenEmpty>
              {(catalog) => {
                // Codes ciblés hydratés (Step 4) mais absents des actifs :
                // assureur désactivé/inconnu. Invariant R1 — restent visibles,
                // retirables, non réajoutables, libellé lisible « (inactif) ».
                const inactiveTargets = targets.filter((c) => !catalog.isActive(c));
                return (
                  <div className="flex flex-wrap gap-2">
                    {catalog.activeOptions.map((ins) => {
                      const active = targets.includes(ins.value);
                      return (
                        <button
                          key={ins.value}
                          type="button"
                          onClick={() => toggleInsurer(ins.value)}
                          aria-pressed={active}
                          className={
                            active
                              ? "border-brand-500 bg-brand-100 text-brand-800 focus-visible:ring-brand-500 rounded-full border px-3 py-1 text-xs font-medium focus-visible:outline-none focus-visible:ring-2"
                              : "focus-visible:ring-brand-500 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs text-slate-600 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2"
                          }
                        >
                          {ins.label}
                        </button>
                      );
                    })}
                    {inactiveTargets.map((code) => (
                      <button
                        key={code}
                        type="button"
                        onClick={() => toggleInsurer(code)}
                        aria-pressed={true}
                        className="focus-visible:ring-brand-500 rounded-full border border-slate-300 bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600 focus-visible:outline-none focus-visible:ring-2"
                      >
                        {catalog.labelFor(code)}
                      </button>
                    ))}
                  </div>
                );
              }}
            </InsurerCatalogBoundary>
          </fieldset>

          <div>
            <label htmlFor="qr-title" className="block text-xs font-medium text-slate-600">
              {t("intake_events.detail.materialize.wizard.step_5_target_insurers.title_label")}
            </label>
            <input
              id="qr-title"
              type="text"
              maxLength={200}
              value={data.createQuoteRequest?.title ?? ""}
              onChange={(e) =>
                onChange({
                  createQuoteRequest: {
                    ...(data.createQuoteRequest ?? {}),
                    title: e.target.value,
                  },
                })
              }
              className="focus-visible:border-brand-500 focus-visible:ring-brand-500 mt-1 w-full rounded border border-slate-300 p-2 text-sm focus-visible:outline-none focus-visible:ring-2"
            />
          </div>
        </div>
      )}
    </section>
  );
}
